import { type FormEvent, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormErrorTypes,
  Input,
  Modal,
  useFormState,
  useFormValidation,
  ModalContext,
  useServerResponseTranslation,
} from 'dash-ui';

import { DashValidation, isEmptyOrNil, isNameLengthValid } from 'dash-common';
import {
  EditMediaFileDocument,
  useEditMediaFileMutation,
} from 'gql/generated.graphql';

enum FormFields {
  Name = 'name',
}

const FormFieldsList = [FormFields.Name];

function EditMediaFileModal() {
  const { t } = useTranslation(['common', 'forms']);
  const formRef = useRef<HTMLFormElement>(null);
  const { isOpenCurrent, toggleModal, setPayload, payload } =
    useContext(ModalContext);
  const [formErrors, , [setNameError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [formState, [setName], [formOnSubmit, formOnError, , , resetForm]] =
    useFormState(FormFieldsList);
  const translateErrorMessage = useServerResponseTranslation();

  const [editMediaFileMutation, { loading }] = useEditMediaFileMutation({
    refetchQueries: [EditMediaFileDocument],
  });
  const isOpen = !!isOpenCurrent['EditMediaFileModal'];

  // init default value
  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    setName(payload?.name || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const onModalClose = () => {
    toggleModal(false, 'EditMediaFileModal');
    setPayload(null);
    resetFormErrors();
    resetForm();
  };

  const validName = (value: string) => {
    if (isEmptyOrNil(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t`forms:messages:nameBlank`,
      });
      return false;
    }
    if (!isNameLengthValid(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
      return false;
    }
    setNameError(null);
    return true;
  };
  const onValueTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    validName(e.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { name } = formState;
    if (!validName(name)) {
      return null;
    }
    editMediaFileMutation({
      variables: {
        fileId: payload._id,
        input: { name },
      },
    })
      .then(onModalClose)
      .catch((error) => {
        formOnError(translateErrorMessage(error));
      });
    return null;
  };

  return (
    <Modal
      title={t('pages:propertyPage:editMediaModalLabel') || ''}
      isOpen={isOpen}
      onRequestClose={onModalClose}
      submitButton={{
        w: '100%',
        title: t('common:labels:save'),
        isLoading: loading,
        disabled: loading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        w: '100%',
        title: t('common:labels:cancel'),
        onClick: onModalClose,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={1}>
          <Form.Label required>{t('forms:labels:name')}</Form.Label>
          <Input
            w="100%"
            value={formState.name}
            placeholder={t('forms:placeholders:mediaName') || ''}
            onChange={onValueTextChange}
            $errorType={formErrors[FormFields.Name]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
            {formErrors[FormFields.Name]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group mt={4}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
}

export default EditMediaFileModal;
